import React, { useState } from 'react';
import { Upload as UploadIcon, FileText, CheckCircle, AlertCircle } from 'lucide-react';

// API Gateway 엔드포인트 URL
const UPLOAD_API_URL = 'https://kfcfxmo8td.execute-api.ap-northeast-1.amazonaws.com/prod/upload-url';

interface UploadResponse {
  uploadType: string;
  url: string;
  fields: { [key: string]: string };
  fileKey: string;
  bucketName: string;
  expiresIn: number;
}

// Legacy interface for backward compatibility
interface LegacyUploadResponse {
  presignedUrl: string;
  fileKey: string;
  bucketName: string;
  expiresIn: number;
}

const Upload: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setUploadStatus('idle');
    setUploadProgress(0);
    setErrorMessage('');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    fileInput?.click();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadStatus('uploading');
    setUploadProgress(0);
    setErrorMessage('');
    
    try {
      // Step 1: API Gateway에서 pre-signed URL 요청
      console.log('Requesting pre-signed URL...');
      const response = await fetch(UPLOAD_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: selectedFile.name,
          fileType: selectedFile.type,
        }),
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const data = await response.json();
      
      setUploadProgress(25);

      // Step 2: Pre-signed URL을 사용해 S3에 파일 업로드
      console.log('Uploading file to S3...');
      
      // Check if it's the new POST format or legacy PUT format
      if (data.uploadType === 'POST' && data.url && data.fields) {
        // New POST method
        console.log('Using POST method with form data');
        const formData = new FormData();
        
        // Add all the fields from the pre-signed POST
        Object.keys(data.fields).forEach(key => {
          formData.append(key, data.fields[key]);
        });
        
        // Add the file last
        formData.append('file', selectedFile);

        const uploadResponse = await fetch(data.url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`S3 upload failed: ${uploadResponse.status}`);
        }
        
        console.log('File uploaded successfully:', data.fileKey);
      } else if (data.presignedUrl) {
        // Legacy PUT method (backward compatibility)
        console.log('Using legacy PUT method');
        const uploadResponse = await fetch(data.presignedUrl, {
          method: 'PUT',
          body: selectedFile,
          headers: {
            'Content-Type': selectedFile.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`S3 upload failed: ${uploadResponse.status}`);
        }
        
        console.log('File uploaded successfully:', data.fileKey);
      } else {
        throw new Error('Invalid response format.');
      }

      setUploadProgress(100);
      setUploadStatus('success');
      
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setErrorMessage('');
    // 파일 input 초기화
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const getStatusMessage = () => {
    switch (uploadStatus) {
      case 'uploading':
        return `Uploading file... (${uploadProgress}%)`;
      case 'success':
        return 'File uploaded successfully!';
      case 'error':
        return `File upload failed: ${errorMessage}`;
      default:
        return '';
    }
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">ファイルアップロード</h1>
        <p className="text-gray-600">署名付きURLを使用してファイルをS3バケットにアップロードします。</p>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="max-w-2xl mx-auto">
          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              isDragOver
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <UploadIcon className="w-8 h-8 text-blue-600" />
              </div>
              
              {selectedFile ? (
                <div className="mb-6">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <span className="text-gray-900 font-medium">{selectedFile.name}</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    サイズ: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <p className="text-sm text-gray-500">
                    種類: {selectedFile.type || 'Unknown'}
                  </p>
                </div>
              ) : (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">アップロードするファイルを選択</h3>
                  <p className="text-gray-600">ここにファイルをドラッグ＆ドロップするか、クリックして参照します</p>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handleBrowseClick}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center space-x-2"
                >
                  <UploadIcon className="w-4 h-4" />
                  <span>ファイルを参照</span>
                </button>
                
                {selectedFile && (
                  <button
                    onClick={handleReset}
                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    削除
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* File Type Info */}
          <div className="mt-4 text-sm text-gray-500 text-center">
            <p>Supported file formats: All file types are supported</p>
            <p>Maximum file size: 100MB</p>
            <p className="mt-2 text-blue-600">Target bucket: x7k9m2p4v8n1q3z6</p>
          </div>

          {/* Upload Progress */}
          {uploadStatus === 'uploading' && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2 text-center">アップロードの進行状況</h3>
              <p className="text-center text-gray-600 mb-4">ファイルをアップロードしています。しばらくお待ちください。</p>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Upload Button and Status */}
          {selectedFile && (
            <div className="mt-8 text-center">
              <button
                onClick={handleUpload}
                disabled={uploadStatus === 'uploading' || uploadStatus === 'success'}
                className="w-full max-w-xs mx-auto bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg disabled:bg-green-300 disabled:cursor-not-allowed"
              >
                {uploadStatus === 'uploading' ? 'アップロード中...' : '今すぐアップロード'}
              </button>
              
              {uploadStatus !== 'idle' && (
                <div className="mt-4 flex items-center justify-center space-x-2">
                  {getStatusIcon()}
                  <p className="text-sm text-gray-600">{getStatusMessage()}</p>
                </div>
              )}
            </div>
          )}

          {/* Success Message */}
          {uploadStatus === 'success' && (
            <div className="mt-8 text-center p-6 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex justify-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">アップロード完了！</h3>
              <p className="text-gray-700 mb-6">ファイルは正常にアップロードされました。</p>
              <button
                onClick={handleReset}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                別のファイルをアップロード
              </button>
            </div>
          )}

          {/* Error Message */}
          {uploadStatus === 'error' && (
            <div className="mt-8 text-center p-6 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex justify-center mb-4">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">アップロード失敗</h3>
              <p className="text-gray-700 mb-4">アップロード中に問題が発生しました。</p>
              <p className="text-sm text-red-800 bg-red-100 p-2 rounded-md mb-6">
                <strong>Error:</strong> {errorMessage}
              </p>
              <button
                onClick={handleUpload}
                className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                再試行
              </button>
            </div>
          )}

          {/* Hidden File Input */}
          <input
            id="file-input"
            type="file"
            onChange={handleFileInputChange}
            style={{ 
              display: 'none',
              visibility: 'hidden',
              position: 'absolute',
              left: '-9999px',
              width: '0',
              height: '0',
              opacity: 0
            }}
            accept="*/*"
          />
        </div>
      </div>

      {/* Upload History (placeholder) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Uploads</h3>
        <div className="text-center py-8 text-gray-500">
          <p>No recent uploads to display</p>
          <p className="text-sm mt-2">Uploaded files will appear in S3 bucket: x7k9m2p4v8n1q3z6</p>
        </div>
      </div>
    </div>
  );
};

export default Upload; 